<script setup lang="ts">
import { useFieldArray, useFormContext } from 'vee-validate'
import { computed, nextTick, onMounted, ref } from 'vue'

import type { CustomOrganizationLinkCreation } from '@/types/components/relations/types.ts'

import { useOrganizationOptions } from '@/composables/useOrganizationOptions'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore'
import { translationFunctionBackend } from '@/utilities/translations'

import SectionPersonOrganizationList from './SectionOrganizationList.vue'

const props = defineProps<{
  name: string
}>()

const tfBackend = translationFunctionBackend()

const formOpen = ref(false)

const { organizationOptions, refresh } = useOrganizationOptions()

const { setFieldValue } = useFormContext()
const organizationLinkFieldArray = useFieldArray<CustomOrganizationLinkCreation>(() => {
  return props.name
})

const filteredOrganizationOptions = computed(() => {
  return organizationOptions.value.filter((org) => {
    if (org.optionValue.customer_type === 'Individual') {
      return false
    }
    return !organizationLinkFieldArray.fields.value.some((link) => {
      return org.optionValue.name === link.value.link_name
    })
  })
})

const addOrganizationLink = async (
  organizationCreationLink: CustomOrganizationLinkCreation,
): Promise<void> => {
  organizationLinkFieldArray.push({
    ...organizationCreationLink,
    tw_is_primary_link: organizationLinkFieldArray.fields.value.length === 0 ? 1 : 0,
  })

  formOpen.value = false
  await Promise.resolve()
}

const handleSetAsPrimary = (fieldIndex: number): void => {
  organizationLinkFieldArray.fields.value.forEach((__, iterationIndex) => {
    if (iterationIndex === fieldIndex) {
      setFieldValue(`${props.name}.${iterationIndex.toString()}.tw_is_primary_link`, 1)
      organizationLinkFieldArray.move(iterationIndex, 0)
    } else {
      setFieldValue(`${props.name}.${iterationIndex.toString()}.tw_is_primary_link`, 0)
    }
  })
}

const removeOrganizationLink = async (organisationCreationItemIndex: number): Promise<void> => {
  const deletePrimary =
    organizationLinkFieldArray.fields.value[organisationCreationItemIndex]?.value.tw_is_primary_link

  await nextTick(() => {
    organizationLinkFieldArray.remove(organisationCreationItemIndex)
  })
  if (deletePrimary === 1 && organizationLinkFieldArray.fields.value.length > 0) {
    await nextTick(() => {
      setFieldValue(`${props.name}.0.tw_is_primary_link`, 1)
    })
  }
}

const backendConstantsStore = useBackendConstantsStore()
const { fetchRoleList } = backendConstantsStore

onMounted(async () => {
  await fetchRoleList()
  await refresh()
})
const displayedOrgs = computed(() => {
  return organizationLinkFieldArray.fields.value.map((org) => {
    return {
      description: org.value.tw_description,
      doctype: org.value.link_doctype,
      image: org.value.image,
      isPrimary: org.value.tw_is_primary_link === 1,
      key: org.value.link_name,
      linkPath: `/relationships/organizations/${org.value.link_name}`,
      linkTitle: org.value.link_title ?? '',
      roleLabel: backendConstantsStore.roleOptionList.find(
        (opt) => opt.optionValue === org.value.tw_role,
      )?.optionLabel,
      tagLabel: tfBackend(`relationType.${org.value.link_doctype}`),
    }
  })
})
</script>

<template>
  <SectionPersonOrganizationList
    :on-add="addOrganizationLink"
    :on-remove="removeOrganizationLink"
    :on-set-primary="handleSetAsPrimary"
    :organization-options="filteredOrganizationOptions"
    :organizations="displayedOrgs"
  />
</template>
